// @todo: Migrate to 'Dialog.Alert' from @wandb/components. For more details see:
// https://wandb.github.io/components/?path=/docs/migration-guide--docs
// eslint-disable-next-line wandb/no-deprecated-imports
import {Alert} from '@wandb/weave/components/Alert';
// @todo: Migrate to 'Icon' from @wandb/components. For more details see:
// https://wandb.github.io/components/?path=/docs/migration-guide--docs
// eslint-disable-next-line wandb/no-deprecated-imports
import {Icon} from '@wandb/weave/components/Icon';
import React, {useCallback, useMemo, useState} from 'react';
// @todo: Migrate to 'Link' from @wandb/components. For more details see:
// https://wandb.github.io/components/?path=/docs/migration-guide--docs
// eslint-disable-next-line wandb/no-deprecated-imports
import {Link} from 'react-router-dom';
import TimeAgo from 'react-timeago';
// eslint-disable-next-line wandb/no-deprecated-imports
import {Feed} from 'semantic-ui-react';

import * as Generated from '../generated/graphql';
import {checkIsServiceAccount} from '../util/serviceAccounts';
import * as Urls from '../util/urls';
import {InstrumentedLoader as Loader} from './../components/utility/InstrumentedLoader';
import * as S from './ArtifactActionHistory.styles';
import ArtifactActionHistoryLinks from './ArtifactActionHistoryLinks';
import {AddAliasButton} from './ArtifactAlias';
import {
  Banner,
  BannerBoldText,
  BannerFlexWrapper,
  BannerTextWrapper,
} from './Banner';
import {useGetMigratedRegistryEntityInfo} from './Registries/ModelRegistryMigration/hooks';
// @todo: Migrate to 'TagGroup' from @wandb/components. For more details see:
// https://wandb.github.io/components/?path=/docs/migration-guide--docs
// eslint-disable-next-line wandb/no-deprecated-imports
import {Tag, TagType} from './Tags';

interface ArtifactActionHistoryProps {
  entityName: string;
  projectName: string;
  artifactTypeName: string;
  artifactCollectionName: string;
  pageSize: number;
}

interface ArtifactAliasActionIconProps {
  actionType: string;
}

type ActionInfo = {
  entityName: string;
  alias: string;
  actionType: string;
  username: string;
  artifactTypeName: string;
  sourceArtifactVersion?: {
    artifactSequence: {
      projectName: string;
      name: string;
    };
    versionIndex: number | null | undefined;
  };
  targetArtifactVersion?: {
    artifactSequence: {
      projectName: string;
      name: string;
    };
    versionIndex: number | null | undefined;
  };
  createdAt: string;
  isServiceAccount: boolean;
};

interface ArtifactAliasActionSummaryProps {
  actionInfo: ActionInfo;
  onAliasClick: (alias: string) => void;
}

interface ArtifactAliasActionEventProps {
  actionInfo: ActionInfo;
  onAliasClick: (alias: string) => void;
}

const ActionEvent: React.FC<ArtifactAliasActionEventProps> = ({
  actionInfo,
  onAliasClick,
}) => {
  return (
    <S.FeedEvent>
      <S.LabelWrapper>
        <Feed.Label>
          <S.IconWrapper>
            <ActionIcon actionType={actionInfo.actionType} />
          </S.IconWrapper>
        </Feed.Label>
      </S.LabelWrapper>

      <S.FeedContent>
        <Feed.Summary>
          <ActionMessage actionInfo={actionInfo} onAliasClick={onAliasClick} />
        </Feed.Summary>
      </S.FeedContent>
    </S.FeedEvent>
  );
};

enum AliasActionType {
  INSERT = 'insert_alias',
  DELETE = 'delete_alias',
  MOVE = 'move_alias',
}

const ActionIcon: React.FC<ArtifactAliasActionIconProps> = ({actionType}) => {
  if (actionType === AliasActionType.INSERT) {
    return <Icon name="add-new" style={{fontSize: '1em'}} />;
  } else if (actionType === AliasActionType.MOVE) {
    return <Icon name="forward-next" />;
  } else if (actionType === AliasActionType.DELETE) {
    return <Icon name="delete" />;
  } else {
    return <Icon name="document" />;
  }
};

const ActionMessage: React.FC<ArtifactAliasActionSummaryProps> = ({
  actionInfo,
  onAliasClick,
}) => {
  const {
    entityName,
    alias,
    actionType,
    username,
    sourceArtifactVersion,
    targetArtifactVersion,
    artifactTypeName,
    createdAt,
    isServiceAccount,
  } = actionInfo;
  const verbs: {[key: string]: string} = {
    [AliasActionType.INSERT]: 'added',
    [AliasActionType.DELETE]: 'deleted',
    [AliasActionType.MOVE]: 'moved',
  };
  const isMove = actionType === AliasActionType.MOVE;
  const targetArtifactUrl = Urls.artifact({
    entityName,
    projectName: targetArtifactVersion?.artifactSequence.projectName ?? '',
    artifactTypeName,
    artifactCollectionName: targetArtifactVersion?.artifactSequence.name ?? '',
    artifactCommitHash: 'v' + targetArtifactVersion?.versionIndex,
  });

  const userUrl = `/${username}`;
  return (
    <>
      <S.UserTime>
        {isServiceAccount ? username : <Link to={userUrl}>{username}</Link>}
        <S.Dot>&#183;</S.Dot>
        <S.CreatedAt>
          <TimeAgo date={createdAt} />
        </S.CreatedAt>
      </S.UserTime>
      <S.Message>
        <S.MessageItems>
          {verbs[actionType]} the alias{' '}
          <S.Alias>
            <Tag
              tag={{
                name: alias,
                colorIndex: TagType.ALIAS,
              }}
              onClick={() => {
                onAliasClick(alias);
              }}
              noun="alias"
            />
          </S.Alias>
          {isMove && sourceArtifactVersion ? (
            <>
              {'from '}
              {sourceArtifactVersion == null ? (
                <>Private Artifact</>
              ) : (
                <S.SpacedLink
                  to={Urls.artifact({
                    entityName,
                    projectName:
                      sourceArtifactVersion.artifactSequence.projectName,
                    artifactTypeName,
                    artifactCollectionName:
                      sourceArtifactVersion.artifactSequence.name,
                    artifactCommitHash:
                      'v' + sourceArtifactVersion.versionIndex,
                  })}>
                  {sourceArtifactVersion.artifactSequence.name +
                    ':v' +
                    sourceArtifactVersion.versionIndex}
                </S.SpacedLink>
              )}
              {'  to  '}
              {targetArtifactVersion == null ? (
                <>Private Artifact</>
              ) : (
                <S.LeftSpacedLink to={targetArtifactUrl}>
                  {'  ' +
                    targetArtifactVersion?.artifactSequence.name +
                    ':v' +
                    targetArtifactVersion?.versionIndex}
                </S.LeftSpacedLink>
              )}
              .
            </>
          ) : (
            <>
              {'on '}
              {targetArtifactVersion == null ? (
                <>Private Artifact</>
              ) : (
                <S.LeftSpacedLink to={targetArtifactUrl}>
                  {'  ' +
                    targetArtifactVersion?.artifactSequence.name +
                    ':v' +
                    targetArtifactVersion?.versionIndex}
                </S.LeftSpacedLink>
              )}
              .
            </>
          )}
        </S.MessageItems>
      </S.Message>
    </>
  );
};

export const ArtifactActionHistory = (props: ArtifactActionHistoryProps) => {
  const {
    entityName,
    projectName,
    artifactTypeName,
    artifactCollectionName,
    pageSize,
  } = props;

  // Model registry migration related information - relevant only for organization
  // registries that were created from a migration
  // Fetch the team name, whose legacy model registry was migrated
  const {legacyMREntityName, migrationCompletedAt, isMigratedRegistryProject} =
    useGetMigratedRegistryEntityInfo(entityName, projectName);
  const redactedEntityName =
    legacyMREntityName ?? '(name hidden due to permissions)';

  const [offset, setOffset] = useState(1);
  const [selectedAliases, setSelectedAliases] = useState<string[]>([]);

  const {data, loading} =
    Generated.useArtifactCollectionAliasActionHistoryQuery({
      variables: {
        entityName,
        projectName,
        artifactTypeName,
        artifactCollectionName,
      },
    });

  const globalAliasActions = useMemo(() => {
    const actions =
      data?.project?.artifactType?.artifactCollection?.aliasActionHistory
        ?.edges ?? [];
    return actions.map(n => {
      const isServiceAccount = checkIsServiceAccount(n.node.user?.accountType);
      const nameText =
        (isServiceAccount ? n.node.user?.name : n.node.user?.username) ?? '';
      return {
        alias: n.node.alias,
        actionType: n.node.actionType,
        entityName,
        artifactTypeName,
        username: nameText,
        targetArtifactVersion: n.node.targetArtifact
          ? {
              artifactSequence: {
                name: n.node.targetArtifact.artifactSequence.name,
                projectName:
                  n.node.targetArtifact.artifactSequence?.project?.name ?? '',
              },
              versionIndex: n.node.targetArtifact?.versionIndex,
            }
          : undefined,
        sourceArtifactVersion: n.node.sourceArtifact
          ? {
              artifactSequence: {
                name: n.node.sourceArtifact.artifactSequence.name,
                projectName:
                  n.node.sourceArtifact.artifactSequence?.project?.name ?? '',
              },
              versionIndex: n.node.sourceArtifact?.versionIndex,
            }
          : undefined,
        createdAt: n.node.createdAt,
        isServiceAccount,
      };
    });
  }, [data, entityName, artifactTypeName]);

  const selectedAliasActions = useMemo(() => {
    let actions = globalAliasActions;
    if (selectedAliases.length > 0) {
      actions = actions.filter(a => selectedAliases.includes(a.alias));
    }
    return actions;
  }, [globalAliasActions, selectedAliases]);

  const currentPaginatedAliasActions = useMemo(() => {
    return selectedAliasActions.filter(
      (a, i) =>
        i >= (offset - 1) * pageSize && i < pageSize + (offset - 1) * pageSize
    );
  }, [selectedAliasActions, offset, pageSize]);

  const currentCollectionAliases = useMemo(() => {
    const aliasDict: {[key: string]: boolean} = {};
    globalAliasActions.forEach(a => {
      aliasDict[a.alias] = true;
    });
    return Object.keys(aliasDict).map(n => {
      return {
        alias: n,
      };
    });
  }, [globalAliasActions]);

  const resolveTotalPages = useCallback(() => {
    if (selectedAliases.length > 0) {
      const len = selectedAliasActions.length;
      if (len >= pageSize && len % pageSize === 0) {
        return len / pageSize;
      }
      return Math.trunc(len / pageSize) + 1;
    }
    return Math.trunc(globalAliasActions.length / pageSize) + 1;
  }, [selectedAliases, selectedAliasActions, globalAliasActions, pageSize]);

  const onAliasClick = (alias: string) => {
    if (!selectedAliases.includes(alias)) {
      setSelectedAliases(prevState => [...prevState, alias]);
    }
    setOffset(1);
  };

  // We don't store the migration of a model registry artifact collection as an
  // explicit action. Instead, we fetch an organization's migration status and
  // display a banner to tell users when the collection was migrated.
  const isMigratedArtifactCollection =
    data?.project?.artifactType?.artifactCollection
      ?.isModelRegistryMigratedArtifactCollection ?? false;
  const modelRegistryMigrationBanner = (
    <Banner variant="info" style={{margin: '0 32px'}}>
      <BannerFlexWrapper>
        <div>
          <Icon name="lightbulb-info" />
        </div>
        <BannerTextWrapper>
          <span>
            <BannerBoldText>
              This collection and parent registry were migrated.{' '}
            </BannerBoldText>
            The legacy model registry of team, "{redactedEntityName}" (including
            this collection) was migrated to the new W&B Registry on{' '}
            {migrationCompletedAt}.
          </span>
        </BannerTextWrapper>
      </BannerFlexWrapper>
    </Banner>
  );
  const showMigrationBanner =
    isMigratedRegistryProject && isMigratedArtifactCollection;

  if (loading) {
    return <Loader name={'artifact-action-history'} />;
  }
  return (
    <>
      {selectedAliasActions.length === 0 ? (
        <S.ArtifactActionsContainer>
          {showMigrationBanner && modelRegistryMigrationBanner}
          <Alert severity="info" style={{margin: '8px'}}>
            No history has been recorded yet for this collection.
          </Alert>
        </S.ArtifactActionsContainer>
      ) : (
        <S.ArtifactActionsContainer>
          {showMigrationBanner && modelRegistryMigrationBanner}
          <S.ArtifactActionsWrapper>
            <div style={{background: 'white', padding: '16px 32px 32px 32px'}}>
              <S.Header>
                <S.HeaderText>Aliases</S.HeaderText>
                <S.AliasHeader>
                  {selectedAliases.map(alias => (
                    // eslint-disable-next-line react/jsx-key
                    <Tag
                      tag={{
                        name: alias,
                        colorIndex: TagType.ALIAS,
                      }}
                      onDelete={() => {
                        setSelectedAliases(prevState =>
                          [...prevState].filter(a => a !== alias)
                        );
                      }}
                      noun="alias"
                    />
                  ))}
                </S.AliasHeader>
                <AddAliasButton
                  aliases={currentCollectionAliases}
                  availableAliases={currentCollectionAliases}
                  onCreate={alias => {
                    if (
                      currentCollectionAliases.find(a => a.alias === alias) &&
                      !selectedAliases.includes(alias)
                    ) {
                      setSelectedAliases(prevState => [...prevState, alias]);
                    }
                    return Promise.resolve();
                  }}
                />
              </S.Header>
              <S.RowsContainer>
                {currentPaginatedAliasActions.map((a, i) => {
                  return (
                    <ActionEvent
                      actionInfo={a}
                      onAliasClick={onAliasClick}
                      key={`${i}` + a.alias + a.createdAt}
                    />
                  );
                })}
              </S.RowsContainer>
              <S.Paginator
                activePage={offset}
                onPageChange={(e: any, value: any) => {
                  setOffset(value.activePage as number);
                }}
                size="mini"
                totalPages={resolveTotalPages()}
              />
            </div>
          </S.ArtifactActionsWrapper>
          <ArtifactActionHistoryLinks
            entityName={entityName}
            projectName={projectName}
            artifactTypeName={artifactTypeName}
            artifactCollectionName={artifactCollectionName}
            pageSize={10}
            membershipNoun={'link'}
          />
        </S.ArtifactActionsContainer>
      )}
    </>
  );
};

export default ArtifactActionHistory;
